import { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, radii, shadows, space, type } from '~/ui/tokens';
import { formatIsoShort } from '../lib/dateUtils';
import { formatGuestsLabel } from '../lib/guests';
import { detectUserCountry } from '~/lib/currency';
import { normalizeResidency, setDefaultResidency } from '~/lib/residency';
import { GuestsPanel } from './GuestsPanel';
import { shortCountryCode } from './NationalityList';
import { ReferenceDateRangePicker } from './ReferenceDateRangePicker';
import {
  POPULAR_DESTINATIONS,
  SuggestionRow,
  useDestinationSuggestions,
  type Place,
  type SearchBarValue,
} from './ReferenceSearchBar';

type Field = 'destination' | 'checkin' | 'checkout' | 'guests';

interface Rect { x: number; y: number; width: number; height: number }

interface Props {
  value: SearchBarValue;
  onChange: (value: SearchBarValue) => void;
  onSearch: () => void;
  onSelectPlace?: (place: Place, search: SearchBarValue) => void;
  /** Open straight away (results page with no nationality yet). */
  autoExpand?: boolean;
  navHeight: number;
}

const DATE_PANEL_WIDTH = 680;
const GUESTS_PANEL_WIDTH = 384;
const DESTINATION_PANEL_WIDTH = 480;
const EDGE = 16;

function nightsBetween(start: string | null, end: string | null) {
  if (!start || !end) return 0;
  return Math.max(0, Math.round((Date.parse(end) - Date.parse(start)) / 86400000));
}

/**
 * The desktop nav search: one bar that is always the whole form. Clicking a
 * segment opens the page dim and that segment's dropdown; the bar is redrawn at
 * the same spot inside the dim so it never moves or changes size.
 */
export function NavSearchDesktop({ value, onChange, onSearch, onSelectPlace, autoExpand = false, navHeight }: Props) {
  const { width: windowWidth } = useWindowDimensions();
  const barRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<Field | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [nationalityError, setNationalityError] = useState(false);
  const cloneRef = useRef<View>(null);
  const slotRef = useRef<View>(null);

  // Keyboard: opening from the header moves focus onto the same segment in the
  // dimmed copy of the bar (the destination input focuses itself), and closing
  // returns it to the header bar.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (open && active && active !== 'destination') {
      const timer = setTimeout(() => {
        const clone = cloneRef.current as unknown as HTMLElement | null;
        const label = active === 'checkin' ? 'Check-in' : active === 'checkout' ? 'Check-out' : 'Guests and rooms';
        clone?.querySelector<HTMLElement>(`[aria-label="${label}"]`)?.focus();
      }, 60);
      return () => clearTimeout(timer);
    }
    if (!open && document.activeElement === document.body) {
      (slotRef.current as unknown as HTMLElement | null)?.querySelector<HTMLElement>('[role="button"]')?.focus();
    }
  }, [open, active]);
  const suggestions = useDestinationSuggestions(value.destination);
  const [cursor, setCursor] = useState<number | null>(null);
  const destinationInputRef = useRef<TextInput>(null);
  const showingPopular = value.destination.trim() === '' || Boolean(value.selectedDestination && value.selectedDestination.label === value.destination);
  const listed = showingPopular ? POPULAR_DESTINATIONS : suggestions;

  // Arrow keys move through the dropdown, Enter picks the highlighted row (or the first).
  function handleDestinationKey(event: { nativeEvent?: { key?: string }; key?: string; preventDefault?: () => void }) {
    const key = event.key ?? event.nativeEvent?.key;
    if (key === 'ArrowDown' && listed.length) {
      event.preventDefault?.();
      setCursor((index) => (index == null ? 0 : Math.min(index + 1, listed.length - 1)));
    } else if (key === 'ArrowUp' && listed.length) {
      event.preventDefault?.();
      setCursor((index) => (index == null ? listed.length - 1 : Math.max(index - 1, 0)));
    } else if (key === 'Enter') {
      // preventDefault stops the input's own Enter handling (onSubmitEditing) from picking twice.
      const chosen = listed[cursor ?? 0];
      if (chosen) {
        event.preventDefault?.();
        pickPlace(chosen);
      }
    }
  }

  function openAt(field: Field | null) {
    const node = barRef.current;
    if (!node) return;
    node.measureInWindow((x, y, width, height) => {
      setRect({ x, y, width, height });
      setActive(field);
      setOpen(true);
    });
  }

  useEffect(() => {
    if (!autoExpand) return;
    setNationalityError(true);
    const timer = setTimeout(() => openAt('guests'), 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExpand]);

  function close() {
    setOpen(false);
    setActive(null);
  }

  function handleSearch() {
    // No destination: keep the panel open on the destination field instead of searching.
    if (!value.destination.trim() && !value.selectedDestination) {
      setActive('destination');
      destinationInputRef.current?.focus();
      return;
    }
    close();
    onSearch();
  }

  function pickPlace(place: Place) {
    const next = { ...value, destination: place.label, selectedDestination: place };
    onChange(next);
    onSelectPlace?.(place, next);
    setActive('checkin');
  }

  const nights = nightsBetween(value.dates.start, value.dates.end);

  function segmentStyle(field: Field, flex: number) {
    return [styles.segment, { flex }, active === field && open && styles.segmentActive];
  }

  function Divider({ hidden }: { hidden: boolean }) {
    return <View style={[styles.divider, hidden && styles.dividerHidden]} />;
  }

  function renderBar(interactive: boolean) {
    const isOn = (f: Field) => interactive && active === f;
    const destinationEditing = isOn('destination');
    return (
      <View style={styles.bar}>
        <Pressable
          accessibilityRole={destinationEditing ? undefined : 'button'}
          accessibilityLabel="Destination"
          onPress={() => { if (!destinationEditing) (interactive ? setActive('destination') : openAt('destination')); }}
          style={segmentStyle('destination', 1.15)}
          // While editing, the segment holds an input and a clear button, so it
          // must not itself be a <button> (no nested buttons).
          {...(destinationEditing ? ({ role: 'group' } as object) : {})}
        >
          <Ionicons name="location-outline" size={20} color={colors.inkSoft} />
          {destinationEditing ? (
            <>
              <TextInput
                ref={destinationInputRef}
                autoFocus
                selectTextOnFocus
                value={value.destination}
                onChangeText={(destination) => { setCursor(null); onChange({ ...value, destination, selectedDestination: null }); }}
                onSubmitEditing={() => { const chosen = listed[cursor ?? 0]; if (chosen) pickPlace(chosen); }}
                onKeyPress={handleDestinationKey}
                {...({ onKeyDown: handleDestinationKey } as object)}
                placeholder="Where to?"
                placeholderTextColor={colors.inkFaint}
                returnKeyType="search"
                style={styles.input}
              />
              {value.destination ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Clear destination"
                  onPress={() => onChange({ ...value, destination: '', selectedDestination: null })}
                  hitSlop={8}
                >
                  <Ionicons name="close-circle" size={18} color={colors.inkSoft} />
                </Pressable>
              ) : null}
            </>
          ) : (
            <Text style={[styles.value, !value.destination && styles.placeholder]} numberOfLines={1}>{value.destination || 'Where to?'}</Text>
          )}
        </Pressable>

        <Divider hidden={isOn('destination') || isOn('checkin')} />

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Check-in"
          onPress={() => (interactive ? setActive('checkin') : openAt('checkin'))}
          style={segmentStyle('checkin', 1.05)}
        >
          <Ionicons name="calendar-outline" size={20} color={colors.inkSoft} />
          <Text style={[styles.value, !value.dates.start && styles.placeholder]} numberOfLines={1}>{value.dates.start ? formatIsoShort(value.dates.start) : 'Check-in'}</Text>
        </Pressable>

        <Divider hidden={isOn('checkin') || isOn('checkout')} />

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Check-out"
          onPress={() => (interactive ? setActive('checkout') : openAt('checkout'))}
          style={segmentStyle('checkout', 1.05)}
        >
          <Ionicons name="calendar-outline" size={20} color={colors.inkSoft} />
          <Text style={[styles.value, !value.dates.end && styles.placeholder]} numberOfLines={1}>{value.dates.end ? formatIsoShort(value.dates.end) : 'Check-out'}</Text>
        </Pressable>

        <Divider hidden={isOn('checkout') || isOn('guests')} />

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Guests and rooms"
          onPress={() => (interactive ? setActive('guests') : openAt('guests'))}
          style={segmentStyle('guests', 1.95)}
        >
          <Ionicons name="people-outline" size={20} color={colors.inkSoft} />
          <Text style={styles.value} numberOfLines={1}>{formatGuestsLabel(value.guests)}{normalizeResidency(value.residency) ? ` · ${shortCountryCode(normalizeResidency(value.residency))}` : ''}</Text>
        </Pressable>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Search hotels"
          onPress={interactive ? handleSearch : () => openAt(null)}
          style={({ pressed }) => [styles.searchButton, pressed && styles.searchButtonPressed]}
        >
          <Ionicons name="search" size={18} color={colors.card} />
          <Text style={styles.searchButtonText}>Search</Text>
        </Pressable>
      </View>
    );
  }

  function panelLeft(panelWidth: number, alignRight = false) {
    if (!rect) return EDGE;
    const preferred = alignRight ? rect.x + rect.width - panelWidth : rect.x;
    return Math.max(EDGE, Math.min(preferred, windowWidth - panelWidth - EDGE));
  }

  const dropdownTop = rect ? rect.y + rect.height + 10 : navHeight;
  const showingDates = active === 'checkin' || active === 'checkout';

  return (
    <>
      <View ref={(node) => { (barRef as { current: View | null }).current = node; (slotRef as { current: View | null }).current = node; }} collapsable={false} style={[styles.slot, { maxWidth: Math.min(780, windowWidth - 540) }]}>
        {renderBar(false)}
      </View>

      <Modal visible={open} transparent animationType="none" presentationStyle="overFullScreen" statusBarTranslucent onRequestClose={close}>
        <Pressable style={StyleSheet.absoluteFill} onPress={close}>
          <View style={[styles.dim, { top: navHeight }]} />
        </Pressable>

        {rect ? (
          <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
            <View ref={cloneRef} style={{ position: 'absolute', left: rect.x, top: rect.y, width: rect.width, height: rect.height }}>
              {renderBar(true)}
            </View>

            {active === 'destination' ? (
              <View style={[styles.dropdown, { top: dropdownTop, left: panelLeft(DESTINATION_PANEL_WIDTH), width: DESTINATION_PANEL_WIDTH }]}>
                <ScrollView style={styles.destinationScroll} keyboardShouldPersistTaps="handled">
                  {showingPopular ? (
                    <Text style={styles.listHeading}>Popular destinations</Text>
                  ) : null}
                  {listed.map((place, index) => (
                    <SuggestionRow
                      key={`${place.kind}-${place.hid ?? place.id ?? place.label}`}
                      place={place}
                      active={index === cursor}
                      committed={value.selectedDestination?.kind === place.kind && value.selectedDestination.label === place.label}
                      onPress={() => pickPlace(place)}
                    />
                  ))}
                </ScrollView>
              </View>
            ) : null}

            {showingDates ? (
              <View style={[styles.dropdown, { top: dropdownTop, left: panelLeft(DATE_PANEL_WIDTH), width: DATE_PANEL_WIDTH }]}>
                <ReferenceDateRangePicker
                  value={value.dates}
                  onChange={(dates) => onChange({ ...value, dates })}
                  onApply={handleSearch}
                  mode={active === 'checkin' ? 'checkin' : 'checkout'}
                  applyLabel={nights > 0 ? `Search · ${nights} night${nights === 1 ? '' : 's'}` : 'Search'}
                />
              </View>
            ) : null}

            {active === 'guests' ? (
              <View style={[styles.dropdown, { top: dropdownTop, left: panelLeft(GUESTS_PANEL_WIDTH, true), width: GUESTS_PANEL_WIDTH }]}>
                <GuestsPanel
                  visible
                  value={value.guests}
                  onChange={(guests) => onChange({ ...value, guests })}
                  onClose={() => setActive(null)}
                  nationality={value.residency}
                  suggestedNationality={normalizeResidency(detectUserCountry()) || 'in'}
                  onNationalityChange={(code) => {
                    setDefaultResidency(code);
                    setNationalityError(false);
                    onChange({ ...value, residency: code });
                  }}
                  promptNationality={nationalityError}
                />
              </View>
            ) : null}
          </View>
        ) : null}
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  // The pill is centred, so it shrinks only half as fast as the window; its max width
  // is capped inline (windowWidth - 540) to keep the logo and account area clear.
  slot: {
    width: '100%',
    maxWidth: 780,
  },
  bar: {
    height: 56,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 4,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.full,
    backgroundColor: colors.card,
    ...shadows.sm,
  },
  segment: {
    height: 48,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    paddingHorizontal: space.compact,
    borderWidth: 2,
    borderColor: 'transparent',
    borderRadius: radii.full,
  },
  segmentActive: {
    borderColor: colors.brand,
    backgroundColor: colors.card,
  },
  divider: {
    width: 1,
    height: 24,
    backgroundColor: colors.line,
  },
  dividerHidden: {
    opacity: 0,
  },
  value: {
    flexShrink: 1,
    fontFamily: fontFamily.medium,
    fontSize: type.bodyRegular.fontSize,
    color: colors.ink,
  },
  placeholder: {
    color: colors.inkSoft,
  },
  input: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    fontFamily: fontFamily.medium,
    fontSize: type.bodyRegular.fontSize,
    color: colors.ink,
    ...({ outlineStyle: 'none' } as object),
  },
  searchButton: {
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    paddingHorizontal: space.internal,
    marginLeft: space.micro,
    borderRadius: radii.full,
    backgroundColor: colors.brand,
  },
  searchButtonPressed: {
    opacity: 0.9,
  },
  searchButtonText: {
    fontFamily: fontFamily.semibold,
    fontSize: type.bodyRegular.fontSize,
    color: colors.card,
  },
  dim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
  },
  dropdown: {
    position: 'absolute',
    backgroundColor: colors.card,
    borderRadius: radii.lg,
    overflow: 'hidden',
    ...shadows.lift,
  },
  destinationScroll: {
    maxHeight: 420,
    padding: space.compact,
  },
  listHeading: {
    paddingHorizontal: space.compact,
    paddingVertical: space.related,
    fontFamily: fontFamily.semibold,
    fontSize: type.bodyRegular.fontSize,
    color: colors.inkSoft,
  },
});
